import { memo, useMemo } from 'react';
import type { FC } from 'react';
import { SettingsIcon } from '@smolt/shared/components';
import type { CameraState, SessionState } from '@smolt/shared/types';

export interface CameraCardFooterProps {
  cameraState: CameraState;
  sessionState: SessionState;
  onSettingsClick: () => void;
  onActionClick: () => void;
  isMobile?: boolean;
}

export const CameraCardFooter: FC<CameraCardFooterProps> = memo(
  ({
    cameraState,
    sessionState,
    onSettingsClick,
    onActionClick,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility
    isMobile: _isMobile = false,
  }) => {
    const handleSettingsClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onSettingsClick();
    };

    const handleActionClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onActionClick();
    };

    // Determine button state from primitive values
    const buttonState = useMemo(():
      | 'recording'
      | 'starting'
      | 'ready'
      | 'stopping'
      | 'disabled' => {
      if (sessionState === 'counting') {
        return 'recording';
      }
      if (sessionState === 'starting') {
        return 'starting';
      }
      if (sessionState === 'stopping') {
        return 'stopping';
      }
      if (cameraState === 'playing' && sessionState === 'ready') {
        return 'ready';
      }
      return 'disabled';
    }, [cameraState, sessionState]);

    const buttonSize = 'clamp(24px, 18.18cqi, 56px)';

    // Memoized Recording button (Red circle with white square)
    const recordingButton = useMemo(
      () => (
        <span
          className="relative block"
          style={{ width: buttonSize, height: buttonSize }}
        >
          <span
            className="absolute left-0 top-0 rounded-full"
            style={{
              width: buttonSize,
              height: buttonSize,
              background: '#FF3B30',
            }}
          />
          <span
            className="absolute"
            style={{
              left: '30%',
              right: '30%',
              top: '30%',
              bottom: '30%',
              background: '#FFFFFF',
              borderRadius: '2px',
            }}
          />
        </span>
      ),
      [buttonSize]
    );

    // Memoized Starting/Stopping button (Custom spinner with dynamic size)
    const transitionButton = useMemo(
      () => (
        <div
          style={{
            width: buttonSize,
            height: buttonSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            viewBox="0 0 100 100"
            style={{
              width: buttonSize,
              height: buttonSize,
              shapeRendering: 'geometricPrecision',
            }}
          >
            <circle
              cx="50"
              cy="50"
              r="50"
              fill="#FF9500"
            />
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (360 / 8) * i;
              const rad = (Math.PI / 180) * angle;
              const ring = 22;
              const rx = 11;
              const ry = 4;
              const cx = 50 + ring * Math.cos(rad);
              const cy = 50 + ring * Math.sin(rad);

              return (
                <g
                  key={i}
                  transform={`translate(${cx} ${cy}) rotate(${angle})`}
                >
                  <rect
                    x={-rx}
                    y={-ry}
                    width={rx * 2}
                    height={ry * 2}
                    rx={ry}
                    ry={ry}
                    fill="#FFFFFF"
                    style={{
                      animation: `petalPulse 1.2s linear infinite`,
                      animationDelay: `${(1.2 / 8) * i}s`,
                      opacity: 1,
                    }}
                  />
                </g>
              );
            })}
            <style>
              {`
            @keyframes petalPulse {
              0%   { fill: #FFFFFF; opacity: 1;   }
              10%  { fill: #FFFFFF; opacity: 0.6; }
              100% { fill: #FFFFFF; opacity: 0.01; }
            }
          `}
            </style>
          </svg>
        </div>
      ),
      [buttonSize]
    );

    // Memoized Ready button (Green circle with white triangle)
    const readyButton = useMemo(
      () => (
        <span
          className="relative block"
          style={{ width: buttonSize, height: buttonSize }}
        >
          <span
            className="absolute left-0 top-0 rounded-full"
            style={{
              width: buttonSize,
              height: buttonSize,
              background: '#34C759',
            }}
          />
          <span
            className="absolute"
            style={{
              left: '38%',
              top: '25%',
              width: '35%',
              height: '50%',
              background: '#FFFFFF',
              clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
            }}
          />
        </span>
      ),
      [buttonSize]
    );

    const renderButtonContent = () => {
      switch (buttonState) {
        case 'recording':
          return recordingButton;
        case 'starting':
        case 'stopping':
          return transitionButton;
        case 'ready':
          return readyButton;
        default:
          return readyButton;
      }
    };

    const isDisabled = buttonState === 'disabled' || buttonState === 'starting';

    return (
      <>
        <style>
          {`
          .action-button-expanded::before {
            content: '';
            position: absolute;
            top: -25%;
            right: -25%;
            bottom: -25%;
            left: -25%;
            border-radius: 50%;
          }
        `}
        </style>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: '0 0 auto',
          }}
        >
          <button
            onClick={handleSettingsClick}
            style={{
              width: 'clamp(24px, 18.18cqi, 56px)',
              height: 'clamp(24px, 18.18cqi, 56px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#4b5563',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              outline: 'none',
              boxShadow: 'none',
              position: 'relative',
            }}
            className="action-button-expanded"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#374151')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4b5563')}
            aria-label="Settings"
          >
            <span
              style={{
                width: 'clamp(16px, 11.36cqi, 36px)',
                height: 'clamp(16px, 11.36cqi, 36px)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon color="#FFFFFF" />
            </span>
          </button>
          <button
            onClick={handleActionClick}
            disabled={isDisabled}
            style={{
              width: buttonSize,
              height: buttonSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: 'none',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              outline: 'none',
              padding: 0,
              transition: 'opacity 0.2s ease',
              background: 'transparent',
              position: 'relative',
            }}
            className={
              buttonState === 'recording' || buttonState === 'ready'
                ? 'hover:opacity-90 action-button-expanded'
                : 'action-button-expanded'
            }
            aria-label={buttonState === 'recording' ? 'Stop' : 'Start'}
          >
            {renderButtonContent()}
          </button>
        </div>
      </>
    );
  }
);

CameraCardFooter.displayName = 'CameraCardFooter';
